import { forwardRef } from 'react';
import type { LucideIcon } from 'lucide-react';

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: LucideIcon;
  variant?: 'default' | 'minimal' | 'builder';
  placeholderColor?: string;
  multiline?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, error, hint, icon: Icon, className = '', variant = 'default', placeholderColor, style, multiline: _multiline, ...props }, ref) => {
    const hasError = !!error;
    const isMinimal = variant === 'minimal';
    const isBuilder = variant === 'builder';

    return (
      <div className={isMinimal ? '' : 'w-full space-y-1.5'}>
        {label && (
          <label className={
            isMinimal ? 'block text-sm mb-1'
            : isBuilder ? 'block text-xs font-medium text-text-secondary'
            : 'block text-sm font-medium text-text-primary'
          }>
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <div className={`absolute ${isMinimal ? 'left-2' : 'left-4'} inset-y-0 flex items-center ${isMinimal ? 'opacity-50' : 'text-text-secondary'}`}>
              <Icon size={isMinimal ? 16 : 18} />
            </div>
          )}
          <input
            ref={ref}
            className={
              isMinimal
                ? `w-full py-2 px-0 ${Icon ? 'pl-7' : ''} bg-transparent border-0 border-b border-current/30 focus:outline-none focus:border-current disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm ${placeholderColor ? 'luxury-input' : ''} ${className}`
                : isBuilder
                ? `w-full h-9 px-3 ${Icon ? 'pl-10' : ''} bg-background border ${hasError ? 'border-error' : 'border-primary/30'} rounded-lg text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${className}`
                : `w-full h-10 px-4 ${Icon ? 'pl-11' : ''} bg-background-card border ${hasError ? 'border-error' : 'border-border'} rounded-lg text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${placeholderColor ? 'luxury-input' : ''} ${className}`
            }
            style={{ ...(style ?? {}), ...(placeholderColor ? { '--ph-color': placeholderColor } as React.CSSProperties : {}) }}
            {...props}
          />
        </div>
        {(error || hint) && (
          <p className={`text-sm ${hasError ? 'text-error' : 'text-text-secondary'}`}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
